import {
  CarrierVisitDirection,
  FirstAvailableOrderInGroupDto,
  GetFirstAvailableOrderInGroupQueryItem,
  OrderResponseDto,
  OrderStatus,
  PickUpOrderFilterType,
  ReleaseResponseDto,
} from '@planning/app/api'
import { SimpleListStore } from '@planning/components/list/SimpleListStore'
import { orderReleaseService, orderService } from '@planning/services'
import generalCargoService from '@planning/services/generalCargoService'
import _ from 'lodash'
import { action, computed, makeObservable, observable, runInAction } from 'mobx'
import { OrderContainerSearchType, OrderSearchType } from './GateInViewStore'

export class PickUpOrderSearchStore extends SimpleListStore<OrderResponseDto | ReleaseResponseDto> {
  containerSearchType: OrderContainerSearchType = 'containerNumber'
  searchType: OrderSearchType = 'container'
  firstOrderAvailableInGroups: FirstAvailableOrderInGroupDto[] = []
  releaseOrderItems: OrderResponseDto[] = []

  constructor() {
    super(async (filter: string) => {
      if (this.searchType === 'container') {
        if (this.containerSearchType === 'containerNumber') {
          const pickUpOrders = await orderService.pickUpOrders(
            filter,
            PickUpOrderFilterType.ContainerNumber,
          )

          const groups = this.getGroupsFromOrders(pickUpOrders)
          if (groups.length) {
            this.firstOrderAvailableInGroups =
              await orderService.firstAvailableOrderInGroups(groups)
          }

          return pickUpOrders
        }

        if (this.containerSearchType === 'referenceNumber') {
          return await orderReleaseService.getReleasesByReferenceNumber(filter)
        }
      }

      if (this.searchType === 'generalCargo') {
        const generalCargoOrders = await generalCargoService.getByCustomerOrReferenceNumber(
          filter,
          CarrierVisitDirection.Outbound,
        )
        return _(generalCargoOrders.filter(o => !o.carrierVisitId && o.status === OrderStatus.Open))
          .sortBy(o => o.referenceNumber)
          .value()
      }

      return []
    })

    makeObservable(this, {
      containerSearchType: observable,
      searchType: observable,
      firstOrderAvailableInGroups: observable,
      releaseOrderItems: observable,
      orders: computed,
      releases: computed,
      ordersForReferenceNumber: computed,
      setContainerSearchType: action,
      setSearchType: action,
      getReleaseOrders: action,
    })
  }

  private readonly isOrder = (item: OrderResponseDto | ReleaseResponseDto) =>
    'containerNumber' in item

  get orders() {
    return this.items.filter(o => this.isOrder(o)).map(o => o as OrderResponseDto)
  }

  isDummyOrder = (order: OrderResponseDto | ReleaseResponseDto) => {
    return (
      'containerNumber' in order &&
      !order.containerNumber &&
      'commodityId' in order &&
      !order.commodityId
    )
  }

  get ordersForReferenceNumber() {
    const getReferenceKey = (order: OrderResponseDto | ReleaseResponseDto): string => {
      if ('containerNumber' in order) {
        return order.releaseReference ?? order.referenceNumber ?? ''
      }

      return order.referenceNumber ?? ''
    }

    return _(this.items.filter(x => !this.isDummyOrder(x)))
      .orderBy(getReferenceKey)
      .value()
  }

  get releases() {
    return this.items.filter(item => !this.isOrder(item)).map(o => o as ReleaseResponseDto)
  }

  setContainerSearchType = (searchType: OrderContainerSearchType) => {
    this.reset()
    this.setFilter('')
    this.firstOrderAvailableInGroups = []

    this.containerSearchType = searchType
  }

  setSearchType = (searchType: OrderSearchType) => {
    this.reset()
    this.searchType = searchType

    if (searchType === 'generalCargo') {
      this.setContainerSearchType('referenceNumber')
    } else {
      this.setContainerSearchType('containerNumber')
    }
  }

  getGroupsFromOrders = (orders: OrderResponseDto[]) => {
    const groups: GetFirstAvailableOrderInGroupQueryItem[] = []

    orders
      .filter(order => !!order.customerGrouping && !!order.linkedCustomerGroupingVisitId)
      .forEach(order => {
        if (
          !groups.some(
            x =>
              x.customerGrouping === order.customerGrouping &&
              x.linkedCustomerGroupingVisitId === order.linkedCustomerGroupingVisitId,
          )
        ) {
          groups.push({
            customerGrouping: order.customerGrouping,
            linkedCustomerGroupingVisitId: order.linkedCustomerGroupingVisitId!,
          })
        }
      })

    return groups
  }

  getReleaseOrders = async (releaseId: number | null) => {
    if (!releaseId) {
      this.releaseOrderItems = []
      return
    }

    this.setLoading(true)

    const orders = await orderService.pickUpOrders(
      releaseId.toString(),
      PickUpOrderFilterType.NumericReleaseId,
    )

    this.setLoading(false)

    runInAction(() => {
      this.releaseOrderItems = orders
    })
  }
}
